import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Step, EventData, useJoyride } from 'react-joyride'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'
import { useTourGuideStyles } from './use-tour-guide.styles'
import { useTheme } from '@mui/material'

export const DEFAULT_TOUR_TOOLTIP_Z_INDEX = 1000
export interface GuidedTourStep {
  target: Step['target']
  content: Step['content']
  navigateToAfterStep?: string
  placement?: Step['placement']
  skipScroll?: boolean
}

interface CustomTourProps {
  steps: GuidedTourStep[]
  run: boolean
  onTourComplete?: () => void
  scrollingEnabled?: boolean
}

export const GuidedTour: React.FC<CustomTourProps> = ({ steps, run, onTourComplete, scrollingEnabled = false }) => {
  const theme = useTheme()
  const tourGuideStyles = useTourGuideStyles()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useCustomTranslation()
  const [stepIndex, setStepIndex] = useState(0)

  /** Keep track of which step involved navigation and the previous route */
  const navHistory = useRef<Record<number, string>>({})

  const handleCallback = useCallback(
    (data: EventData): void => {
      const { action, index, lifecycle, step } = data

      if (action === 'start' && lifecycle === 'init') {
        /**
         * This state occurs only once per tour. This is where we can save from which
         * tab the user started the tour so we can return to this tab once tour is finished.
         */
        navHistory.current[1] = location.pathname
      }

      if (['finished', 'skipped'].includes(data.status) || data.action === 'close') {
        setStepIndex(0)

        if (onTourComplete) {
          onTourComplete()
        }

        /** on tour finish, return to the tab from which the user started the tour */
        navigate(navHistory.current[1])

        return
      }

      if (lifecycle !== 'complete') return

      if (action === 'next') {
        if (step?.data?.clickable) {
          document.body.click()
        }

        setStepIndex(index + 1)

        if (step?.data?.next) {
          /** Record current path before navigating */
          navHistory.current[index] = location.pathname

          const parts = location.pathname.split('/')
          parts[parts.length - 1] = step.data.next
          const newPath = parts.join('/')
          navigate(newPath)
        }
        return
      }

      if (action === 'prev') {
        const prevIndex = Math.max(index - 1, 0)

        if (navHistory.current[prevIndex]) {
          const prevPath = navHistory.current[prevIndex]
          navigate(prevPath)

          setStepIndex(prevIndex)
        } else {
          setStepIndex(prevIndex)
        }
      }
    },
    [location, navigate, onTourComplete]
  )

  const { controls, Tour } = useJoyride({
    continuous: true,

    steps: steps.map(step => ({
      data: {
        next: step.navigateToAfterStep,
      },
      options: {
        skipBeacon: true,
      },
      ...step,
    })),
    options: {
      dismissKeyAction: 'close',
      overlayClickAction: false,
      primaryColor: theme.palette.primary.main,
      spotlightRadius: theme.shape.borderRadius,
      zIndex: DEFAULT_TOUR_TOOLTIP_Z_INDEX,
      skipScroll: !scrollingEnabled,
    },
    onEvent: handleCallback,
    run: run,
    stepIndex: stepIndex,
    styles: tourGuideStyles,
    locale: { back: t('emobility.tour.back-button'), next: t('emobility.tour.next-button'), last: t('emobility.tour.last-button') },
  })

  const goToNextStep = useCallback(() => {
    if (!steps) {
      return
    }

    /**
     * We are using internal Joyride methods to go the next step using keyboard. However, Joyride by default
     * closes the tour if we attempt to move one step back from the first step.
     *
     * For testing purposes, we want to be able to quickly go to the first and last step, but not close tour unless
     * explicitly clicking "Finish" or "X" button in the step popup. For this reason, we skip triggering the "next"
     * method while in the last step.
     */
    if (stepIndex === steps.length - 1) {
      return
    }

    controls.next()
  }, [controls, stepIndex, steps])

  const goToPreviousStep = useCallback(() => {
    /**
     * We are using internal Joyride methods to go the next step using keyboard. However, Joyride by default
     * closes the tour if we attempt to move one step forward from the first step.
     *
     * For testing purposes, we want to be able to quickly go to the first and last step, but not close tour unless
     * explicitly clicking "Finish" or "X" button in the step popup. For this reason, we skip triggering the "prev"
     * method while in the first step(index 0).
     */
    if (stepIndex === 0) {
      return
    }

    controls.prev()
  }, [stepIndex, controls])

  const closeTour = useCallback(() => {
    /** manually simulate "close" event that occurs when the user presses "Finish" or "X" */
    controls.stop()
  }, [controls])

  /**
   * Handle keyboard navigation:
   *
   * - "left" and "right" arrows to go to previous/next step
   * - "esc" key closes the tour as if the user pressed "X" or "Finish" in tour popup
   *
   * The "keydown" even listener only exists while tour is running.
   * No need to memoize the handleKeyDown function since it is created only once
   * per tour mount.
   */
  useEffect(() => {
    if (!run) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      switch (event.key) {
        case 'ArrowRight':
          goToNextStep()
          break
        case 'ArrowLeft':
          goToPreviousStep()
          break
        case 'Escape':
          closeTour()
          break
        default:
      }
    }

    globalThis.addEventListener('keydown', handleKeyDown)

    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown)
    }
  }, [run, steps, goToNextStep, goToPreviousStep, closeTour])

  return <>{Tour}</>
}
